import { Children, ReactNode, useEffect, useState } from 'react';
import {
  Combobox,
  Group,
  InputBase,
  CheckIcon,
  Tabs,
  TabsProps,
  useCombobox,
  TabsListProps,
  Tooltip,
} from '@mantine/core';

import useUrlState from '@/hooks/useUrlStore';

import ChevronDown from '@/assets/icons/content/chevron-down.svg';

import classes from './Tabs.module.css';

interface Tab {
  tab: ReactNode;
  val: string;
  disabled?: boolean;
  hide?: boolean;
  tooltip?: string;
}

interface MercuryTabsProps extends TabsProps {
  tabs: Array<Tab>;
  id?: string;
  namespace?: string;
  small?: boolean;
  defaultValue?: string;
  saveTabInUrl?: boolean;
  urlObject?: Record<string, any>;
  navigateMode?: 'push' | 'replace';
  sticky?: boolean;
  renderSelect?: boolean;
  grow?: boolean;
  justify?: TabsListProps['justify'];
  availabilityPending?: boolean;
}

const MercuryTabs = ({
  tabs: rawTabs,
  id,
  namespace,
  small,
  children,
  defaultValue: definedDefaultValue,
  saveTabInUrl,
  navigateMode,
  sticky,
  renderSelect,
  urlObject,
  grow,
  justify,
  availabilityPending,
  ...tabsProps
}: MercuryTabsProps) => {
  const [state, actions] = useUrlState({} as any, {
    ...(id ? { namespace } : {}),
    ...(navigateMode ? { navigateMode } : {}),
  });

  const { tab: currentTab } = state;

  const tabs = rawTabs.filter(({ hide }) => !hide);

  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
    onDropdownOpen: (eventSource) => {
      if (eventSource === 'keyboard') {
        combobox.selectActiveOption();
      } else {
        combobox.updateSelectedOptionIndex('active');
      }
    },
  });

  const defaultValue = () => {
    if (currentTab && tabs.find(({ val }) => val === currentTab)) {
      return currentTab;
    }

    return definedDefaultValue || tabs.find((tab) => !tab.disabled)?.val || tabs[0].val;
  };

  const [activeTabState, setActiveTab] = useState<string | null>(defaultValue());
  const activeTab = saveTabInUrl ? (currentTab ?? activeTabState) : activeTabState;
  const selectedOption = tabs.find(({ val }) => val === activeTab);

  const onTabChange = (tab: string | null) => {
    if (saveTabInUrl && tab) {
      actions.set({ tab, ...(urlObject ?? {}) });
    } else {
      setActiveTab(tab);
    }

    if (renderSelect) {
      combobox.closeDropdown();
    }
  };

  const availability = tabs.map(({ val, disabled }) => `${val}:${disabled ? 0 : 1}`).join();

  useEffect(() => {
    if (availabilityPending || !selectedOption?.disabled) return;

    const firstAvailable = tabs.find(({ disabled }) => !disabled);

    if (firstAvailable) {
      onTabChange(firstAvailable.val);
    }
  }, [availability, availabilityPending]);

  const options = tabs.map(({ val, tab, disabled }) => (
    <Combobox.Option value={val} key={val} disabled={disabled} active={val === activeTab}>
      <Group gap="xs">
        {val === activeTab && <CheckIcon size={12} />}
        {tab}
      </Group>
    </Combobox.Option>
  ));

  return (
    <Tabs
      classNames={{
        root: classes.root,
        tab: `${classes.tab} ${small ? classes.small : ''}`,
        list: `${classes.list} ${sticky ? classes.sticky : ''}`,
        panel: classes.panel,
      }}
      id={`tabs-${id}`}
      keepMounted={false}
      onChange={onTabChange}
      value={activeTab}
      {...tabsProps}
    >
      {renderSelect ? (
        <Combobox
          store={combobox}
          data-testid={`tabs-select-${id}`}
          onOptionSubmit={onTabChange}
          withinPortal={false}
        >
          <Combobox.Target>
            <InputBase
              component="button"
              type="button"
              rightSection={<ChevronDown />}
              classNames={{ section: classes.selectSection }}
              onClick={() => combobox.toggleDropdown()}
              rightSectionPointerEvents="none"
              pointer
              multiline
            >
              {selectedOption?.tab}
            </InputBase>
          </Combobox.Target>

          <Combobox.Dropdown>
            <Combobox.Options mah={200} style={{ overflowY: 'auto' }}>
              {options}
            </Combobox.Options>
          </Combobox.Dropdown>
        </Combobox>
      ) : (
        <Tabs.List grow={grow} justify={justify} data-testid={`tabs-list-${id}`}>
          {tabs.map(({ val, tab, tooltip, disabled }) => (
            <Tooltip
              label={tooltip}
              disabled={!tooltip}
              events={{ hover: true, focus: true, touch: true }}
              key={`tab-nav-${val}`}
            >
              <Tabs.Tab value={val} disabled={disabled}>
                {tab}
              </Tabs.Tab>
            </Tooltip>
          ))}
        </Tabs.List>
      )}

      {Children.toArray(children)
        .filter(Boolean)
        .map(
          (child, index) =>
            tabs[index] &&
            children && (
              <Tabs.Panel value={tabs[index].val} key={`tab-content-${tabs[index].val}`}>
                {child}
              </Tabs.Panel>
            )
        )}
    </Tabs>
  );
};

export default MercuryTabs;
